import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { ApoyoVista, FilaVista, TiendaCatalogo } from '../types/metas.types';
import { validarCelda } from '../utils/validacionCelda';
import { CLASE_OVERLAY } from './ui/clases';

interface Props {
  fila: FilaVista | null;
  tiendaPropia: { chain: string; num: number };
  /**
   * Catálogo COMPLETO de tiendas: `mi-alcance.catalogo`, el mismo con que la
   * API valida el apoyo. No sirven `mi-alcance.tiendas` (sólo las del
   * supervisor, y el apoyo es justamente a una que no es suya) ni
   * `/dashboard/catalogo-tiendas`: ése es del ERP, cambia de madrugada
   * —ADUANA desaparece— y trae tiendas que la API rechaza con 400.
   */
  catalogo: TiendaCatalogo[];
  diasApoyoMin?: number;
  /** Días con que abre un apoyo NUEVO (la pantalla de captura pone 7); `null` = vacío. */
  diasPorOmision?: number | null;
  onGuardar: (apoyo: ApoyoVista | null) => void;
  onHide: () => void;
}

const llave = (chain: string, num: number) => `${chain}|${num}`;

/** Apoyo de un colaborador a OTRA tienda: cuál y cuántos días de la semana. */
export const ApoyoDialog: React.FC<Props> = ({
  fila,
  tiendaPropia,
  catalogo,
  diasApoyoMin = 3,
  diasPorOmision = null,
  onGuardar,
  onHide,
}) => {
  const [tienda, setTienda] = useState<string | null>(null);
  const [dias, setDias] = useState<number | null>(null);

  useEffect(() => {
    setTienda(fila?.apoyo ? llave(fila.apoyo.chain, fila.apoyo.num) : null);
    setDias(fila?.apoyo?.dias ?? (fila ? diasPorOmision : null));
  }, [fila, diasPorOmision]);

  const opciones = useMemo(() => {
    const lista = catalogo
      // Sólo se excluye la MISMA tienda (cadena y número): la 118 de TRE es
      // otra tienda que la 118 de TAF, y apoyarla es válido.
      .filter((t) => !(t.chain === tiendaPropia.chain && t.num === tiendaPropia.num))
      .map((t) => ({ label: `${t.chain} ${t.num} · ${t.tienda}`, value: llave(t.chain, t.num), t }));
    // Un apoyo ya guardado a una tienda que salió del catálogo se sigue viendo.
    const a = fila?.apoyo;
    if (a && !lista.some((o) => o.value === llave(a.chain, a.num))) {
      lista.push({
        label: `${a.chain} ${a.num} · ${a.tienda ?? 'fuera del catálogo'}`,
        value: llave(a.chain, a.num),
        t: { chain: a.chain, num: a.num, tienda: a.tienda ?? '' },
      });
    }
    return lista.sort((x, y) => x.t.chain.localeCompare(y.t.chain) || x.t.num - y.t.num);
  }, [catalogo, tiendaPropia, fila]);

  const elegida = opciones.find((o) => o.value === tienda)?.t ?? null;
  const propuesta: ApoyoVista | null =
    elegida && dias !== null ? { chain: elegida.chain, num: elegida.num, dias, tienda: elegida.tienda } : null;
  const errores =
    fila && propuesta
      ? validarCelda({ key: fila.key, nombre: fila.nombre, metaMonto: null, sinMetaMotivo: null, apoyo: propuesta }, tiendaPropia)
      : [];

  const pie = (
    <div className="flex flex-wrap justify-end gap-2">
      {fila?.apoyo && (
        <Button
          label="Quitar apoyo"
          icon="pi pi-trash"
          severity="danger"
          text
          onClick={() => onGuardar(null)}
        />
      )}
      <Button label="Cancelar" text onClick={onHide} />
      <Button
        label="Aplicar"
        icon="pi pi-check"
        disabled={!propuesta || errores.length > 0}
        onClick={() => propuesta && onGuardar(propuesta)}
      />
    </div>
  );

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={fila ? `Apoyo de ${fila.nombre}` : 'Apoyo'}
      visible={!!fila}
      onHide={onHide}
      footer={pie}
      style={{ width: '32rem', maxWidth: '95vw' }}
      modal
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          Días de esta semana que trabajó en otra tienda. El apoyo se registra aquí; su meta sigue siendo la de
          esta tienda.
        </p>
        {!catalogo.length && (
          <Message severity="warn" text="No llegó el catálogo de tiendas: recarga la página para registrar un apoyo." />
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor="apoyo-tienda" className="font-semibold">
            Tienda apoyada
          </label>
          <Dropdown
            inputId="apoyo-tienda"
            value={tienda}
            options={opciones}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setTienda(e.value ?? null)}
            filter
            placeholder="Elige la tienda"
            emptyMessage="Sin tiendas"
            emptyFilterMessage="Ninguna tienda coincide"
            className="w-full"
            panelClassName={CLASE_OVERLAY}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="apoyo-dias" className="font-semibold">
            Días (1 a 7)
          </label>
          <InputNumber
            inputId="apoyo-dias"
            value={dias}
            // En cada tecla, no sólo al salir: con `onValueChange` solo,
            // «Aplicar» seguía deshabilitado tras escribir los días y el primer
            // clic se perdía quitando el foco.
            onChange={(e) => setDias(e.value ?? null)}
            onValueChange={(e) => setDias(e.value ?? null)}
            min={1}
            max={7}
            showButtons
            // − y + a los lados, del alto de la casilla: apiladas medían 48 × 22
            // y se fallaba el toque (auditoría del 22-oct-2026).
            buttonLayout="horizontal"
            decrementButtonIcon="pi pi-minus"
            incrementButtonIcon="pi pi-plus"
            decrementButtonClassName="p-button-outlined"
            incrementButtonClassName="p-button-outlined"
            useGrouping={false}
            className="w-full"
            inputClassName="text-center"
          />
          {dias !== null && dias >= 1 && dias < diasApoyoMin && (
            <small className="text-gray-600">
              Con menos de {diasApoyoMin} días el apoyo se registra, pero no genera bono.
            </small>
          )}
        </div>
        {errores.map((e) => (
          <Message key={e.codigo} severity="error" text={e.mensaje} />
        ))}
      </div>
    </Dialog>
  );
};

export default ApoyoDialog;
